import {
    IGameCopper,
    IGameResult,
    useTopicI18nStore,
} from "@/topic/rogue_5/locales"
import {
    getCharAvatar,
    getRoguelikeBand,
    getRoguelikeItem,
} from "@/utils/assets"
import { isIOS, isWechat, loadImage } from "@/utils/misc"
import { IGameRandomResult } from "../store"
import { drawQRCode } from "./qrCode"
import { drawTextWithLetterSpacing, drawTextWithMultiLine } from "./text"

import SvgCopyright from "../../../../../assets/img/copyright.png"
import SvgArk from "../../../../../assets/img/logo2.png"
import ImgBgNormalNormal from "@rogue_5/assets/img/game/share/bg-personal-normal.jpg"
import ImgBgNormalSpecial from "@rogue_5/assets/img/game/share/bg-personal-special.jpg"
import ImgBgRhodesNormal from "@rogue_5/assets/img/game/share/bg-rhodes-normal.jpg"
import ImgBgRhodesSpecial from "@rogue_5/assets/img/game/share/bg-rhodes-special.jpg"
import qrCodeImg from "@rogue_5/assets/img/game/share/qrcode.png"
import ImgRecruitGroup1 from "@rogue_5/assets/img/game/share/recruit/recruit_group_1.png"
import ImgRecruitGroup2 from "@rogue_5/assets/img/game/share/recruit/recruit_group_2.png"
import ImgRecruitGroup3 from "@rogue_5/assets/img/game/share/recruit/recruit_group_3.png"
import ImgRecruitGroup4 from "@rogue_5/assets/img/game/share/recruit/recruit_group_4.png"
import ImgRecruitGroup5 from "@rogue_5/assets/img/game/share/recruit/recruit_group_5.png"
import ImgTextTexture from "@rogue_5/assets/img/game/share/text-texture.jpg"

const RECRUIT_IMAGE = {
    recruit_group_1: ImgRecruitGroup1,
    recruit_group_2: ImgRecruitGroup2,
    recruit_group_3: ImgRecruitGroup3,
    recruit_group_4: ImgRecruitGroup4,
    recruit_group_5: ImgRecruitGroup5,
}

interface IShareData {
    type: "normal" | "rhodes"
    coins: IGameCopper[]
    result: IGameResult
    uid: string
    doctorName: string
    randomResult: IGameRandomResult | null
}

const THEME_COLOR_NORMAL = "#e85298"
const THEME_COLOR_RHODES = "#5eb4c9"

const drawMask = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number
) => {
    ctx.save()
    const gradient = ctx.createLinearGradient(0, 0, width, 0)
    gradient.addColorStop(0, "rgba(0,0,0,0)")
    gradient.addColorStop(0.1, "rgba(0,0,0,1)")
    gradient.addColorStop(0.9, "rgba(0,0,0,1)")
    gradient.addColorStop(1, "rgba(0,0,0,0)")
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, width, height)
    const gradient2 = ctx.createLinearGradient(0, 0, 0, height)
    ctx.globalCompositeOperation = "source-in"
    gradient2.addColorStop(0, "rgba(0,0,0,0)")
    gradient2.addColorStop(0.1, "rgba(0,0,0,1)")
    gradient2.addColorStop(0.9, "rgba(0,0,0,1)")
    gradient2.addColorStop(1, "rgba(0,0,0,0)")

    ctx.fillStyle = gradient2
    ctx.fillRect(0, 0, width, height)
    ctx.restore()
}

const loadCoins = (ctx: CanvasRenderingContext2D, data: IShareData) => {
    const coins = data.coins
    const coinsPromise = Promise.all(
        coins.map((c) =>
            loadImage(
                `${__ASSETS_HOST}assets/img/static/rogue_5/copper/${c.id}.png`
            )
        )
    )
    const drawCoins = async () => {
        const coins = await coinsPromise
        coins.forEach((c, i) => {
            ctx.drawImage(c, 683 + 185 * i, 298, 185, 185)
        })
    }
    return drawCoins
}

const loadBg = (ctx: CanvasRenderingContext2D, data: IShareData) => {
    const bgPromise = loadImage(
        data.type === "normal"
            ? data.result.special
                ? ImgBgNormalSpecial
                : ImgBgNormalNormal
            : data.result.special
              ? ImgBgRhodesSpecial
              : ImgBgRhodesNormal
    )
    return async () => {
        const bgImage = await bgPromise
        ctx.drawImage(bgImage, 0, 0)
    }
}

const loadResultTitle = (ctx: CanvasRenderingContext2D, data: IShareData) => {
    const titleTexturePromise = loadImage(ImgTextTexture)
    return async () => {
        const title = data.result.title
        ctx.save()
        const titleCanvas = document.createElement("canvas")
        titleCanvas.width = 500
        titleCanvas.height = 78
        const titleCtx = titleCanvas.getContext("2d")
        if (!titleCtx) {
            throw new Error("Failed to get title context")
        }
        const titleTexture = await titleTexturePromise
        const pattern = titleCtx.createPattern(titleTexture, "no-repeat")
        if (data.result.special) {
            // if (pattern) titleCtx.fillStyle = pattern
            if (pattern) titleCtx.fillStyle = "#c17b3d"
            else {
                titleCtx.fillStyle = "#c17b3d"
            }
        } else {
            titleCtx.fillStyle = "#181715"
        }
        // titleCtx.fillStyle = "#c17b3d"

        titleCtx.font = "60px NotoSansKR-B"
        drawTextWithLetterSpacing(titleCtx, title, -2, 248, 65)
        ctx.drawImage(titleCanvas, 710, 493, 500, 78)
        ctx.restore()
    }
}

const breakTextIntoLines = (
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number
) => {
    const words = text.split(" ")
    const lines: string[] = []
    let currentLine = words[0] || ""

    for (let i = 1; i < words.length; i++) {
        const word = words[i]
        const testLine = currentLine + " " + word
        const testWidth = ctx.measureText(testLine).width

        if (testWidth <= maxWidth) {
            currentLine = testLine
        } else {
            lines.push(currentLine)
            currentLine = word
        }
    }
    lines.push(currentLine)
    return lines
}
const breakTextAtApproximateLength = (
    text: string,
    approximateLength: number
) => {
    const words = text.split(" ")
    const lines: string[] = []
    let currentLine = words[0] || ""

    for (let i = 1; i < words.length; i++) {
        const word = words[i]
        const testLine = currentLine + " " + word

        // 如果加上新词后超过近似长度，或者下一个词会让行长度更接近目标，则换行
        if (
            testLine.length > approximateLength &&
            Math.abs(testLine.length - approximateLength) >
                Math.abs(currentLine.length - approximateLength)
        ) {
            lines.push(currentLine)
            currentLine = word
        } else {
            currentLine = testLine
        }
    }
    lines.push(currentLine)
    return lines.join("\n")
}
const drawResultDesc = (ctx: CanvasRenderingContext2D, data: IShareData) => {
    // const desc = data.result.description
    // data.result.description = "한가로이 회랑을 거닐고 연못에서 물놀이를 한다."
    const desc = data.result.description.includes(",")
        ? data.result.description.replace(/([,])/g, "$1\n")
        : breakTextAtApproximateLength(data.result.description, 15)

    ctx.save()

    ctx.font =
        data.result.description.length > 40
            ? `20px "NotoSansKR-B"`
            : `23px "NotoSansKR-B"`

    ctx.fillStyle = data.type === "normal" ? "#ffe07e" : "#f8efd3"
    // ctx.fillRect(960 - width / 2, 636 - 30, width, 70)
    ctx.fillRect(745, 636 - 30, 430, 70)
    ctx.fillStyle =
        data.type === "normal" ? THEME_COLOR_NORMAL : THEME_COLOR_RHODES
    // drawTextWithLetterSpacing(ctx, desc, 0, 970, 656)
    // drawTextWithMultiLine(ctx, desc, 760, 635, 410, 30)
    const lines = desc.split("\n")
    const lineHeight = 30
    const containerWidth = 430
    const startX = 720
    const startY = 635

    // 创建带字母间距的文字绘制函数
    const drawTextWithLetterSpacing = (
        text: string,
        x: number,
        y: number,
        spacing: number
    ) => {
        if (spacing === 0) {
            ctx.fillText(text, x, y)
            return
        }

        let currentX = x
        for (let i = 0; i < text.length; i++) {
            const char = text[i]
            ctx.fillText(char, currentX, y)
            currentX += ctx.measureText(char).width + spacing
        }
    }

    lines.forEach((line, index) => {
        const textWidth = ctx.measureText(line).width
        // 调整宽度计算以包含字母间距的影响
        const adjustedWidth = textWidth + (line.length - 1) * -2
        const centeredX = startX + (containerWidth - adjustedWidth) / 2
        drawTextWithLetterSpacing(
            line,
            centeredX,
            startY + index * lineHeight,
            -1
        )
    })

    ctx.restore()
}

const loadCoinDesc = (ctx: CanvasRenderingContext2D, data: IShareData) => {
    const text: IGameCopper["text"] = data.coins.reduce(
        (acc, curr, index) => {
            const tail = index === data.coins.length - 1 ? "." : ", "
            acc["good"] += curr.text.good + tail
            acc["bad"] += curr.text.bad + tail
            acc["career"] += curr.text.career + tail
            acc["wealth"] += curr.text.wealth + tail
            acc["emotion"] += curr.text.emotion + tail
            return acc
        },
        {
            good: "",
            bad: "",
            career: "",
            wealth: "",
            emotion: "",
        }
    )
    if (data.type === "rhodes") {
        const resultRhodes = useTopicI18nStore.getState().objects.resultRhodes
        const good = resultRhodes[data.result.id].good
        const bad = resultRhodes[data.result.id].bad
        if (Array.isArray(good)) {
            text.good = good[Math.floor(Math.random() * good.length)]
        } else {
            text.good = good
        }
        if (Array.isArray(bad)) {
            text.bad = bad[Math.floor(Math.random() * bad.length)]
        } else {
            text.bad = bad
        }
    }
    // const text = {
    //     good: "随遇而安，以柔克，随遇而安，以柔克刚，随遇而安，以柔克刚，",
    //     bad: "随遇而安，以柔克，随遇而安，以柔克刚，随遇而安，以柔克刚，",
    //     career: "山雨欲来风满楼，然则乌云未必成雷。谨慎前行，或可拨云见日。",
    //     wealth: "流水遇石生涟漪，然终归江海。财来财去，随缘而动，不必强求。",
    //     emotion: "明月千里寄相思，近水未必解渴。欲问情缘，且看风向。",
    // }
    let relics: Promise<HTMLImageElement>[] = []
    let characters: Promise<HTMLImageElement>[] = []
    let band: Promise<HTMLImageElement> | null = null
    let recruitGroup: Promise<HTMLImageElement> | null = null
    if (data.type === "rhodes") {
        relics =
            data.randomResult?.relics?.map((r) =>
                loadImage(getRoguelikeItem(r))
            ) || []
        characters =
            data.randomResult?.characters.map((c) =>
                loadImage(getCharAvatar(c))
            ) || []
        band = data.randomResult?.band
            ? loadImage(getRoguelikeBand(data.randomResult.band))
            : null
        recruitGroup = data.randomResult?.recruitGroup
            ? loadImage(RECRUIT_IMAGE[data.randomResult.recruitGroup])
            : null
    }

    return async () => {
        if (data.type === "rhodes") {
            const relicsImages = await Promise.all(relics)
            const charactersImages = await Promise.all(characters)
            const bandImage = await band
            const recruitGroupImage = await recruitGroup
            charactersImages.forEach((c, i) => {
                const canvas = document.createElement("canvas")
                const ctx2 = canvas.getContext("2d")!
                canvas.width = c.width
                canvas.height = c.height
                drawMask(ctx2, canvas.width, canvas.height)
                ctx2.globalCompositeOperation = "source-in"
                ctx2.drawImage(c, 0, 0)
                ctx.drawImage(canvas, 850 + 90 * i, 724, 85, 85)
            })
            if (bandImage) {
                const canvas = document.createElement("canvas")
                const ctx2 = canvas.getContext("2d")!
                canvas.width = 95
                canvas.height = 95
                // drawMask(ctx2, canvas.width, canvas.height)
                const gradient = ctx2.createRadialGradient(
                    47,
                    47,
                    0,
                    47,
                    47,
                    47
                )
                gradient.addColorStop(0, "rgba(20,20,20,0.7)")
                gradient.addColorStop(0.2, "rgba(20,20,20,0.7)")
                gradient.addColorStop(1, "rgba(20,20,20,0)")
                ctx2.fillStyle = gradient
                ctx2.fillRect(0, 0, canvas.width, canvas.height)
                ctx.drawImage(canvas, 1230, 720, 95, 95)
                ctx.drawImage(bandImage, 1230, 720, 95, 95)
            }
            if (recruitGroupImage) {
                ctx.drawImage(recruitGroupImage, 1350, 722, 90, 90)
            }
            relicsImages.forEach((r, i) => {
                ctx.drawImage(r, 500 + 88 * i, 724, 85, 85)
            })
        }
        ctx.save()
        ctx.fillStyle =
            data.type === "normal" ? THEME_COLOR_NORMAL : THEME_COLOR_RHODES
        ctx.font = `19px "NotoSansKR-B"`

        const drawTextWithWordBreak = (
            ctx: CanvasRenderingContext2D,
            text: string,
            x: number,
            y: number,
            maxWidth: number,
            lineHeight: number
        ) => {
            const lines = breakTextIntoLines(ctx, text, maxWidth)
            lines.forEach((line, index) => {
                // 左对齐：直接使用传入的 x 坐标，不需要居中计算
                ctx.fillText(line, x, y + index * lineHeight)
            })
        }

        drawTextWithWordBreak(
            ctx,
            text.good,
            430,
            text.good.length > 20 ? 610 : 632,
            282,
            20
        )
        drawTextWithWordBreak(
            ctx,
            text.bad,
            1216,
            text.bad.length > 20 ? 610 : 632,
            282,
            20
        )
        ctx.font = `18px "NotoSansKR-B"`
        if (data.type !== "rhodes") {
            drawTextWithWordBreak(
                ctx,
                text.career,
                490,
                text.career.length > 22 ? 745 : 764,
                295,
                20
            )
            drawTextWithWordBreak(
                ctx,
                text.wealth,
                840,
                text.wealth.length > 22 ? 745 : 764,
                295,
                20
            )
            drawTextWithWordBreak(
                ctx,
                text.emotion,
                1190,
                text.emotion.length > 22 ? 745 : 764,
                295,
                20
            )
        }

        ctx.restore()
    }
}

const drawDoctorName = (ctx: CanvasRenderingContext2D, data: IShareData) => {
    ctx.save()
    ctx.fillStyle = "#f3efdb"
    ctx.font = `22px "NotoSansKR-B"`
    ctx.fillText(data.doctorName, 490, 860)
    ctx.font = `22px "NotoSansKR-B"`
    const now = new Date()
    const year = now.getFullYear()
    const month = now.getMonth() + 1
    const day = now.getDate()
    const text = `${year}년 ${month}월 ${day}일`
    const width = ctx.measureText(text).width
    ctx.fillText(text, 1476 - width, 861)

    ctx.restore()
}

const loadCopyright = (ctx: CanvasRenderingContext2D, data: IShareData) => {
    const arknightsPromise = loadImage(SvgArk)
    const copyrightPromise = loadImage(SvgCopyright)
    return async () => {
        ctx.save()
        const arknights = await arknightsPromise
        const copyright = await copyrightPromise
        const canvas = document.createElement("canvas")
        canvas.width = 272
        canvas.height = 145
        const ctxInner = canvas.getContext("2d")
        if (!ctxInner) {
            throw new Error("Failed to get canvas context")
        }
        ctxInner.drawImage(arknights, 0, 0, 272, 145)
        ctxInner.globalCompositeOperation = "source-in"
        ctxInner.fillStyle = "#000"
        ctxInner.fillRect(0, 0, 272, 145)
        ctx.drawImage(canvas, 63, 52)
        ctx.drawImage(copyright, 1600, 1018, 207 * 1.3, 13 * 1.3)
        // const qrCodeSrc = await drawQRCode({ uid: data.uid, size: 10 })
        const qrCode = await loadImage(qrCodeImg)
        ctx.drawImage(qrCode, 53, 876, 160, 160)
        ctx.restore()
    }
}

export const drawShareImage = async (data: IShareData, share = false) => {
    const canvas = document.createElement("canvas")
    canvas.width = 1920
    canvas.height = 1080
    const ctx = canvas.getContext("2d")
    if (!ctx) {
        throw new Error("Failed to get canvas context")
    }

    const drawBg = loadBg(ctx, data)
    const drawCoins = loadCoins(ctx, data)
    const drawResultTitle = loadResultTitle(ctx, data)
    const drawCopyright = loadCopyright(ctx, data)
    const drawCoinDesc = await loadCoinDesc(ctx, data)

    await drawBg()
    await drawCoins()
    await drawResultTitle()
    await drawCoinDesc()

    drawResultDesc(ctx, data)
    drawDoctorName(ctx, data)
    if (share) {
        await drawCopyright()
    }
    if (isWechat() && !isIOS()) {
        return canvas.toDataURL("image/png")
    } else
        return new Promise<string>((resolve) => {
            canvas.toBlob((blob) => {
                if (blob) {
                    const url = URL.createObjectURL(blob)
                    resolve(url)
                }

                resolve("")
            })
        })
}
